import { readActiveCustomerFromDocument } from "@/lib/mapper/active-customer-cookie";

/**
 * Browser API client. Every call goes to the same origin (`/api/...`): the web
 * server proxies it to the NestJS backend, and the httpOnly `proly_session`
 * cookie authenticates it. The `x-proly-client` header is required by the
 * backend for cookie-authenticated writes (CSRF guard).
 */

export type ApiFetchInit = Omit<RequestInit, "body"> & {
  body?: BodyInit | null;
  json?: unknown;
  cacheTtlMs?: number;
};

type TtlCacheEntry<T> = {
  expiresAt: number;
  value?: T;
  promise?: Promise<T>;
};

const responseCache = new Map<string, TtlCacheEntry<unknown>>();

/** Fired when the backend says the session is missing/expired. */
export const UNAUTHORIZED_EVENT = "proly:unauthorized";

function toUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith("/") ? path : `/${path}`;
}

function isFormDataBody(body: unknown): body is FormData {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function messageFromErrorPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const data = payload as Record<string, unknown>;
  const message = data.message;
  if (typeof message === "string" && message.length > 0) return message;
  if (Array.isArray(message)) {
    const parts = message.filter((item): item is string => typeof item === "string");
    if (parts.length > 0) return parts.join(", ");
  }
  const error = data.error;
  if (typeof error === "string" && error.length > 0) return error;
  return null;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, data: unknown, fallbackMessage?: string) {
    super(messageFromErrorPayload(data) ?? fallbackMessage ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

function buildRequestInit(init: ApiFetchInit): RequestInit {
  const headers = new Headers(init.headers);
  headers.set("x-proly-client", "web");
  const workspaceId = readActiveCustomerFromDocument();
  if (workspaceId && !headers.has("x-workspace-id")) {
    headers.set("x-workspace-id", workspaceId);
  }

  const { cacheTtlMs: _ttl, json, ...requestInit } = init;
  let body = init.body;
  if (json !== undefined) {
    body = JSON.stringify(json);
    if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  } else if (body != null && !isFormDataBody(body) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return { ...requestInit, headers, body, credentials: "same-origin" };
}

async function uncachedApiFetchResponse(
  path: string,
  init: ApiFetchInit = {},
): Promise<Response> {
  const response = await fetch(toUrl(path), buildRequestInit(init));
  if (!response.ok) {
    const payload = await parseResponseBody(response);
    if (response.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(response.status, payload, response.statusText);
  }
  return response;
}

function cacheKey(path: string, init: ApiFetchInit): string {
  return `${init.method ?? "GET"} ${toUrl(path)} ${readActiveCustomerFromDocument() ?? ""}`;
}

async function cachedJson<T>(path: string, init: ApiFetchInit, ttlMs: number): Promise<T> {
  const key = cacheKey(path, init);
  const now = Date.now();
  const cached = responseCache.get(key) as TtlCacheEntry<T> | undefined;
  if (cached?.value !== undefined && cached.expiresAt > now) return cached.value;
  if (cached?.promise) return cached.promise;

  const promise = uncachedApiFetchResponse(path, init)
    .then((response) => parseResponseBody(response) as Promise<T>)
    .then((value) => {
      responseCache.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    })
    .catch((err) => {
      responseCache.delete(key);
      throw err;
    });

  responseCache.set(key, { promise, expiresAt: now + ttlMs });
  return promise;
}

export function clearApiCache(match?: string | RegExp): void {
  if (!match) {
    responseCache.clear();
    return;
  }
  for (const key of responseCache.keys()) {
    if (typeof match === "string" ? key.includes(match) : match.test(key)) {
      responseCache.delete(key);
    }
  }
}

export async function apiFetchResponse(path: string, init: ApiFetchInit = {}): Promise<Response> {
  return uncachedApiFetchResponse(path, init);
}

export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  if (method === "GET" && init.cacheTtlMs && init.cacheTtlMs > 0) {
    return cachedJson<T>(path, init, init.cacheTtlMs);
  }
  const response = await uncachedApiFetchResponse(path, init);
  return parseResponseBody(response) as Promise<T>;
}

function bodyInit(body: unknown): Pick<ApiFetchInit, "body" | "json"> {
  if (body === undefined) return {};
  if (body === null) return { json: null };
  if (
    typeof body === "string" ||
    isFormDataBody(body) ||
    (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer)
  ) {
    return { body: body as BodyInit };
  }
  return { json: body };
}

export const api = {
  get<T>(path: string, init: ApiFetchInit = {}) {
    return apiFetch<T>(path, { ...init, method: "GET" });
  },
  post<T>(path: string, body?: unknown, init: ApiFetchInit = {}) {
    return apiFetch<T>(path, { ...init, method: "POST", ...bodyInit(body) });
  },
  put<T>(path: string, body?: unknown, init: ApiFetchInit = {}) {
    return apiFetch<T>(path, { ...init, method: "PUT", ...bodyInit(body) });
  },
  patch<T>(path: string, body?: unknown, init: ApiFetchInit = {}) {
    return apiFetch<T>(path, { ...init, method: "PATCH", ...bodyInit(body) });
  },
  delete<T>(path: string, init: ApiFetchInit = {}) {
    return apiFetch<T>(path, { ...init, method: "DELETE" });
  },
};
